<template>
    <div class="flex h-fit w-full min-w-0 flex-col gap-4 md:w-2/5 lg:sticky lg:top-4">
        <!--
            AI panel. Rendered from `md` up, where it sits beside the gallery; below that the phone
            bottom sheet owns this content instead once a photo is in play. Gated on hasPanelContent
            too: Panel's bordered shell renders regardless of its slot content, so with every block
            hidden (no session yet, no style additions) this would otherwise show as an empty
            bordered sliver above the general panel. hasPanelContent is isMounted-gated throughout,
            so this is false pre-mount on both SSR and the client — hydration never mismatches, and
            the panel simply pops in once mounted state says there is something to show.
        -->
        <!--
            The wrapper is the panel's place in the layout, held whether or not the panel is in it.
            Empty it costs nothing; before the page paints, the pre-paint restore fills it with the
            panel the customer left behind, so the column has its real height and its real content
            from the first frame instead of growing one in as Vue mounts. Vue then hydrates the real
            panel into it — see useAiPaintRegions.
        -->
        <!-- display:contents so the wrapper is not a flex item of its own: this column is gap-4, and
             an empty box here would open that gap above the panel for every visitor, generation or
             no generation. What goes in it becomes the flex item instead, exactly as the panel was. -->
        <div ref="aiPanelRegion" data-ai-standin="panel" class="contents">
            <AiPanelBody v-if="showInlinePanel && hasPanelContent">
                <template #after-photo-selection>
                    <!--
                        Only while it is still the way in. This slot sits under the photo row, which is
                        where "generate with this photo" belongs — but the same button turns into the
                        order button the moment a generation exists, and ordering belongs with the price
                        rather than above the name field. It goes back inline there; see
                        showButtonInline, which is the other half of keeping exactly one mounted.
                    -->
                    <AiUploadButton v-if="!generationStarted" />
                </template>
            </AiPanelBody>
        </div>

        <!-- General panel: product options, price, order -->
        <Panel>
            <!-- Visual copy of the title on desktop; the page's only h1 is MobileProductTitle at the top. -->
            <p
                aria-hidden="true"
                :class="['mt-0 font-semibold wrap-break-word max-lg:hidden', titleSize]"
                v-text="productPageStore.configurableProduct.name"
            />

            <!-- Render product reviews -->
            <ProductReviews :review-score="reviewScore" :review-count="reviewCount" />
            <TrustedShopsProductTitleWidgets v-if="!isMounted || isDesktop" class="mt-2 max-lg:hidden" />

            <!-- Related products slider (siblings in the same group, sorted by sales) -->
            <RelatedProductsSlider :items="page.props.related_products ?? []" :show-price="false" :show-arrows="false" :max-items="10" />

            <!-- Render product switch options -->
            <ProductSwitchOptions />

            <!-- Product filters -->
            <ProductFilters />

            <!-- Render in stock -->
            <ProductInStock />

            <!--
                Render product price. On phones it is hidden once the bottom sheet takes over (the
                sheet footer shows it instead) — before that, the plain product info is on screen and
                the price belongs here, same as the non-AI product page.
            -->
            <div :class="{ 'max-md:hidden': showMobileSheet }">
                <ProductPrice />
            </div>

            <!-- Render product delivery information -->
            <ProductDeliveryInformation />

            <!--
                Upload / order button. On phones it lives here until a photo is in play, at which
                point the sheet footer takes over instead; from `md` up it moves into the AI panel
                above whenever the photo-selection block is showing — so exactly one instance of it
                is ever mounted.
            -->
            <div v-if="showButtonInline" class="mt-5" :class="{ 'max-md:hidden': !isMounted }">
                <AiUploadButton />
            </div>

            <!-- Render Klarna promotion badge -->
            <ProductKlarna :price="productStore.selectedProduct.product.data.product_price" />

            <!-- Render discount blocks below the Klarna OSM block -->
            <DiscountBlocks :blocks="productPageStore.discountBlocks" />

            <!-- Render product timer (only on mobile) -->
            <ProductTimer />

            <!-- Render product USPs -->
            <ProductUsps />
        </Panel>

        <ProductShippingOptions />
    </div>
</template>

<script setup lang="ts">
import DiscountBlocks from '@/components/discount-blocks/DiscountBlocks.vue';
import Panel from '@/components/panels/Panel.vue';
import RelatedProductsSlider from '@/components/products/RelatedProductsSlider.vue';
import TrustedShopsProductTitleWidgets from '@/components/reviews/TrustedShopsProductTitleWidgets.vue';
import { useAiPaintRegion } from '@/composables/ai-upload/useAiPaintRegions';
import { useAiPanelState } from '@/composables/ai-upload/useAiPanelState';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import AiPanelBody from '@/pages/ai-product-page/partials/ai-panel/AiPanelBody.vue';
import AiUploadButton from '@/pages/ai-product-page/partials/ai-upload/AiUploadButton.vue';
import ProductDeliveryInformation from '@/pages/product-page/partials/product-info/partials/ProductDeliveryInformation.vue';
import ProductFilters from '@/pages/product-page/partials/product-info/partials/ProductFilters.vue';
import ProductInStock from '@/pages/product-page/partials/product-info/partials/ProductInStock.vue';
import ProductKlarna from '@/pages/product-page/partials/product-info/partials/ProductKlarna.vue';
import ProductPrice from '@/pages/product-page/partials/product-info/partials/ProductPrice.vue';
import ProductReviews from '@/pages/product-page/partials/product-info/partials/ProductReviews.vue';
import ProductShippingOptions from '@/pages/product-page/partials/product-info/partials/ProductShippingOptions.vue';
import ProductSwitchOptions from '@/pages/product-page/partials/product-info/partials/ProductSwitchOptions.vue';
import ProductTimer from '@/pages/product-page/partials/product-info/partials/ProductTimer.vue';
import ProductUsps from '@/pages/product-page/partials/product-info/partials/ProductUsps.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { AppPageProps } from '@/types';
import type { CollectionSliderItemObject } from '@/types/generated';
import { usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

type AiProductInfoProps = {
    reviewScore?: number;
    reviewCount?: number;
};

defineProps<AiProductInfoProps>();

const { isMounted, showPhotoSelectionBlock, generationStarted, isRegenerateBlockDisabled, hasPanelContent } = useAiPanelState();
const { isDesktop, isTabletUp } = useAppearanceComposable();

const aiPanelRegion = ref<HTMLElement | undefined>(undefined);

useAiPaintRegion('panel', aiPanelRegion);

const showInlinePanel = computed<boolean>(() => !isMounted.value || isTabletUp.value);

// A carried photo from a related style, primed but not yet generated, also belongs in the sheet —
// it is where the customer taps "Generate" and sees the photo they are about to use.
const showMobileSheet = computed<boolean>(() => generationStarted.value || showPhotoSelectionBlock.value);

const showButtonInline = computed<boolean>(() => {
    if (!isTabletUp.value) {
        return !showMobileSheet.value;
    }

    return showInlinePanel.value && (!showPhotoSelectionBlock.value || generationStarted.value);
});

const page = usePage<AppPageProps & { related_products?: CollectionSliderItemObject[] }>();
const productPageStore = useProductPageStore();
const productStore = useProductStore();
const titleSize = computed(() => (productPageStore.configurableProduct.name.length > 21 ? 'text-2xl' : 'text-3xl'));
</script>
